/** Descendant propagation helpers for task mutation updates. */
// @ts-check
import {
  getTaskBatch,
  commitBatch,
  updateTaskInBatch,
} from '@/services/taskMutations';

/**
 * @param {string} taskId
 * @param {Task | undefined} originalTask
 * @param {Task[]} allTasks
 * @param {any[]} taskTree
 * @param {(taskId: string, taskTree: any[]) => string[]} getAllDescendantIds
 * @returns {string[]}
 */
export const getDescendantIds = (
  taskId,
  originalTask,
  allTasks,
  taskTree,
  getAllDescendantIds,
) => {
  const pathPrefix = originalTask?.materializedPath || null;
  return pathPrefix
    ? allTasks
        .filter(
          (t) => t.id !== taskId && t.materializedPath?.startsWith(pathPrefix),
        )
        .map((t) => t.id)
    : getAllDescendantIds(taskId, taskTree);
};

/**
 * @param {string} taskId
 * @param {string} statusValue
 * @param {Task | undefined} originalTask
 * @param {Task[]} allTasks
 * @param {any[]} taskTree
 * @param {(taskId: string, taskTree: any[]) => string[]} getAllDescendantIds
 */
export const propagateStatusToDescendants = async (
  taskId,
  statusValue,
  originalTask,
  allTasks,
  taskTree,
  getAllDescendantIds,
) => {
  const descendantIds = getDescendantIds(
    taskId,
    originalTask,
    allTasks,
    taskTree,
    getAllDescendantIds,
  );
  if (descendantIds.length === 0) return;
  const batch = getTaskBatch();
  const descendantUpdate = { status: statusValue };
  for (const descendantId of descendantIds) {
    updateTaskInBatch(batch, descendantId, descendantUpdate);
  }
  await commitBatch(batch);
};

/**
 * @param {string} taskId
 * @param {string[]} projectsValue
 * @param {Task | undefined} originalTask
 * @param {Task[]} allTasks
 * @param {any[]} taskTree
 * @param {(taskId: string, taskTree: any[]) => string[]} getAllDescendantIds
 */
export const propagateProjectsToDescendants = async (
  taskId,
  projectsValue,
  originalTask,
  allTasks,
  taskTree,
  getAllDescendantIds,
) => {
  const descendantIds = getDescendantIds(
    taskId,
    originalTask,
    allTasks,
    taskTree,
    getAllDescendantIds,
  );
  if (descendantIds.length === 0) return;
  const batch = getTaskBatch();
  const descendantUpdate = { projects: projectsValue };
  for (const descendantId of descendantIds) {
    updateTaskInBatch(batch, descendantId, descendantUpdate);
  }
  await commitBatch(batch);
};

/**
 * @param {string} taskId
 * @param {UpdateTask} details
 * @param {Task | undefined} originalTask
 * @param {Task[]} allTasks
 * @param {any[]} taskTree
 * @param {(taskId: string, taskTree: any[]) => string[]} getAllDescendantIds
 * @param {(taskId: string, details: UpdateTask, userId: string) => Promise<void>} updateTaskDetails
 * @param {string} userId
 */
export const applyDetailUpdates = async (
  taskId,
  details,
  originalTask,
  allTasks,
  taskTree,
  getAllDescendantIds,
  updateTaskDetails,
  userId,
) => {
  await updateTaskDetails(taskId, details, userId);
  if (details.status !== undefined) {
    // @ts-ignore - status is checked for existence
    await propagateStatusToDescendants(
      taskId,
      details.status,
      originalTask,
      allTasks,
      taskTree,
      getAllDescendantIds,
    );
  }
  if (details.projects !== undefined) {
    // @ts-ignore - projects is checked for existence
    await propagateProjectsToDescendants(
      taskId,
      details.projects,
      originalTask,
      allTasks,
      taskTree,
      getAllDescendantIds,
    );
  }
};
